import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import { reactPlugin } from '@datadog/browser-rum-react';
import { getOrCreateCorrelationId } from './utils/observing/correlationId';

datadogRum.init({
  applicationId: import.meta.env.VITE_DATADOG_APPLICATION_ID!,
  clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN!,
  site: import.meta.env.VITE_DATADOG_SITE,
  env: import.meta.env.VITE_ENVIRONMENT,
  service: import.meta.env.VITE_DATADOG_SERVICE_ID,
  version: import.meta.env.VITE_DATADOG_RELEASE_VERSION,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 0,
  trackUserInteractions: false,
  trackResources: true,
  trackLongTasks: true,
  defaultPrivacyLevel: 'mask-user-input',
  plugins: [reactPlugin()],
});

// Generate correlation ID and set as RUM global context.
// No onReady() needed — the npm SDK is ready synchronously after init().
const correlationId = getOrCreateCorrelationId();
datadogRum.setGlobalContextProperty('correlationId', correlationId);

datadogLogs.init({
  clientToken: import.meta.env.VITE_DATADOG_CLIENT_TOKEN!,
  site: import.meta.env.VITE_DATADOG_SITE,
  env: import.meta.env.VITE_ENVIRONMENT,
  service: import.meta.env.VITE_DATADOG_SERVICE_ID,
  sessionSampleRate: 100,
});

const bootstrapApp = async () => {
  // Import session/app only after Datadog init so app-owned config takes precedence.
  const [{ session }, { default: App }] = await Promise.all([import('./common/session.ts'), import('./App.tsx')]);

  datadogLogs.setUser({
    id: session.identity?.id,
    type: session.type,
  });

  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
};

bootstrapApp().catch((err) => {
  datadogRum.addError(err, { context: 'bootstrapApp' });
});
